import React, { useRef, useLayoutEffect } from "react";
import { motion } from "framer-motion";
import Reward from "react-rewards";
import {
  CheckCircle,
  X,
  Sparkles,
  DollarSign,
  CreditCard,
  Hash,
  Calendar,
} from "lucide-react";
import useModal from "hooks/use-modal";
import { formatMoney } from "../../utils/formatMoney";

const ModalSettlementAccepted = (props) => {
  const { account, submitAmount, receiptId, paymentMethod, scheduledDate } = props.modalProps;
  const { closeModal } = useModal();
  const rewardRef = useRef();

  useLayoutEffect(() => {
    // Trigger confetti animation when modal opens
    if (rewardRef.current) {
      rewardRef.current.rewardMe();
    }
  }, []);

  // Animation variants
  const contentAnimation = {
    hidden: { opacity: 0, y: 10 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.3,
        staggerChildren: 0.1,
      },
    },
  };

  const itemAnimation = {
    hidden: { opacity: 0, scale: 0.9 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 15,
      },
    },
  };

  return (
    <div className="flex flex-col h-full max-h-[100vh] sm:max-h-[90vh] p-0">
      {/* Confetti Container */}
      <div className="absolute left-1/2 top-0 -translate-x-1/2 z-50 pointer-events-none">
        <Reward
          ref={rewardRef}
          type="confetti"
          config={{
            startVelocity: 20,
            spread: 90,
            elementCount: 100,
            springAnimation: false,
            colors: ["#10b981", "#34d399", "#6ee7b7", "#a7f3d0", "#d1fae5"],
          }}
        >
          <div />
        </Reward>
      </div>

      {/* Modal Header - Success Green Gradient */}
      <div className="relative bg-gradient-to-r from-green-600 to-emerald-700 px-4 sm:px-6 lg:px-8 py-4 sm:py-6 lg:py-8 flex-shrink-0 sm:rounded-t-2xl">
        <button
          onClick={closeModal}
          className="absolute top-3 right-3 sm:top-4 sm:right-4 p-1.5 sm:p-2 rounded-lg bg-white/10 hover:bg-white/20 transition-colors group z-10"
        >
          <X className="w-4 h-4 sm:w-5 sm:h-5 text-white group-hover:rotate-90 transition-transform duration-300" />
        </button>

        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="flex items-center gap-3 sm:gap-4 pr-10"
        >
          <div className="w-10 h-10 sm:w-12 sm:h-12 lg:w-14 lg:h-14 bg-white/20 backdrop-blur rounded-xl flex items-center justify-center flex-shrink-0">
            <Sparkles className="w-5 h-5 sm:w-6 sm:h-6 lg:w-7 lg:h-7 text-white animate-pulse" />
          </div>
          <div className="min-w-0 flex-1">
            <h2 className="text-lg sm:text-xl lg:text-2xl font-bold text-white">
              Congratulations!
            </h2>
            <p className="text-green-100 text-sm sm:text-base">
              Your offer was accepted
            </p>
          </div>
        </motion.div>
      </div>

      {/* Modal Body */}
      <motion.div
        className="p-4 sm:p-6 lg:p-8 overflow-y-auto flex-1 min-h-0 overscroll-contain bg-gray-900"
        variants={contentAnimation}
        initial="hidden"
        animate="visible"
      >
        {/* Success Message Card */}
        <motion.div
          variants={itemAnimation}
          className="bg-green-500/10 border border-green-500/30 rounded-xl p-6 mb-6"
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-green-500/20 rounded-full flex items-center justify-center">
              <CheckCircle className="w-6 h-6 text-green-400" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-white">
                Resolution Offer Accepted!
              </h3>
              <p className="text-gray-400 text-sm">
                Your account will be resolved upon payment completion
              </p>
            </div>
          </div>

          <p className="text-gray-300">
            We've accepted your offer. {scheduledDate ? 
              `Your payment will be processed on ${scheduledDate}` : 
              'Your payment has been processed successfully'}, and your account will be marked as resolved.
          </p>
        </motion.div>

        {/* Settlement Details */}
        <motion.div
          variants={itemAnimation}
          className="bg-gray-800/50 backdrop-blur rounded-xl p-6 mb-6 border border-gray-700/50"
        >
          <h4 className="text-white font-semibold mb-4 flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-gray-400" />
            Resolution Offer Details
          </h4>

          <div className="space-y-3">
            <div className="flex justify-between items-center py-2 border-b border-gray-700/50">
              <span className="text-gray-400 text-sm">Resolution Amount</span>
              <span className="text-xl font-bold text-green-400">
                ${formatMoney(submitAmount)}
              </span>
            </div>

            <div className="flex justify-between items-center py-2 border-b border-gray-700/50">
              <span className="text-gray-400 text-sm">Payment Method</span>
              <span className="text-white">{paymentMethod}</span>
            </div>

            {scheduledDate && (
              <div className="flex justify-between items-center py-2 border-b border-gray-700/50">
                <span className="text-gray-400 text-sm flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  Scheduled Date
                </span>
                <span className="text-white">{scheduledDate}</span>
              </div>
            )}

            {receiptId && (
              <div className="flex justify-between items-center py-2">
                <span className="text-gray-400 text-sm flex items-center gap-1">
                  <Hash className="w-4 h-4" />
                  Confirmation ID
                </span>
                <span className="text-white font-mono">{receiptId}</span>
              </div>
            )}
          </div>
        </motion.div>

        {/* Success Icon Animation */}
        <motion.div
          variants={itemAnimation}
          className="flex justify-center mb-6"
        >
          <motion.div
            animate={{
              scale: [1, 1.2, 1],
              rotate: [0, 360],
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
              repeatType: "reverse",
            }}
            className="w-20 h-20 bg-gradient-to-br from-green-500 to-emerald-600 rounded-full flex items-center justify-center shadow-lg shadow-green-500/30"
          >
            <CheckCircle className="w-10 h-10 text-white" />
          </motion.div>
        </motion.div>

        {/* Important Note */}
        <motion.div
          variants={itemAnimation}
          className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4 mb-6"
        >
          <div className="flex items-start gap-3">
            <CreditCard className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm text-blue-300 font-medium mb-1">
                {scheduledDate ? 'Payment Scheduled' : 'Payment Processed'}
              </p>
              <p className="text-xs text-blue-200">
                {scheduledDate ? 
                  `Your payment will be charged to your selected payment method on ${scheduledDate}. You will receive a confirmation email when the payment is processed.` :
                  'Your payment has been successfully charged to your selected payment method. You should receive a confirmation email shortly.'}
                Please save this confirmation for your records.
              </p>
            </div>
          </div>
        </motion.div>

        {/* Close Button */}
        <motion.div variants={itemAnimation}>
          <button
            onClick={closeModal}
            className="w-full py-3 bg-gradient-to-r from-green-600 to-emerald-700 hover:from-green-700 hover:to-emerald-800 text-white font-semibold rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl flex items-center justify-center gap-2"
          >
            <CheckCircle className="w-5 h-5" />
            Done
          </button>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default ModalSettlementAccepted;
